import React from 'react'
import { cn } from '../lib/utils.ts'

interface SkeletonProps {
  className?: string
}

/** Base skeleton shimmer element */
export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn('skeleton', className)} />
}

/** Skeleton for StatCard component */
export function StatCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('bg-white border border-gray-200 rounded-xl p-6', className)}>
      <div className="flex items-center justify-between">
        <div className="flex-1 space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-10 w-10 rounded-lg" />
      </div>
    </div>
  )
}

/** Skeleton for DonutChartCard component */
export function DonutChartSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('bg-white border border-gray-200 rounded-xl p-6', className)}>
      <Skeleton className="h-4 w-40 mb-1" />
      <Skeleton className="h-3 w-28 mb-4" />
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-10 w-16" />
        <Skeleton className="h-6 w-14 rounded-full" />
      </div>
      <div className="flex items-center justify-center">
        <Skeleton className="h-[180px] w-[180px] rounded-full" />
      </div>
      <div className="mt-4 space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  )
}

/** Column definition for SortableTable */
export interface TableColumn {
  label: string
  key: string
  sortable?: boolean
  hidden?: boolean
  className?: string
  align?: 'left' | 'right' | 'center'
  resizable?: boolean
  title?: string
}

/** Resize handle for column headers */
function ResizeHandle({
  onMouseDown,
}: {
  onMouseDown: (e: React.MouseEvent<HTMLElement>) => void
}) {
  return (
    <span
      onMouseDown={onMouseDown}
      onClick={e => e.stopPropagation()}
      className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-4 h-6 cursor-col-resize select-none group"
      title="Drag to resize"
    >
      <span className="w-px h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
    </span>
  )
}

/** Sortable table header cell (internal — use TableHeader) */
function SortableTh({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
  className,
  align = 'left',
  width,
  onResize,
  title,
}: {
  label: string
  sortKey: string
  currentSort: string
  currentDir: 'asc' | 'desc'
  onSort: (key: string) => void
  className?: string
  align?: 'left' | 'right' | 'center'
  width?: number
  onResize?: (e: React.MouseEvent<HTMLElement>) => void
  title?: string
}) {
  return (
    <th
      className={cn(
        'relative',
        className ||
          cn(
            'px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider',
            align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
          ),
      )}
      style={width ? { width } : undefined}
      title={title}
    >
      <button
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-gray-700 uppercase"
      >
        {label}
        <span className="text-gray-400">
          {currentSort === sortKey ? (currentDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
      {onResize && <ResizeHandle onMouseDown={onResize} />}
    </th>
  )
}

/** Static (non-sortable) table header cell (internal — use TableHeader) */
function StaticTh({
  label,
  className,
  align = 'left',
  width,
  onResize,
  title,
}: {
  label: string
  className?: string
  align?: 'left' | 'right' | 'center'
  width?: number
  onResize?: (e: React.MouseEvent<HTMLElement>) => void
  title?: string
}) {
  return (
    <th
      className={cn(
        'relative',
        className ||
          cn(
            'px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider',
            align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
          ),
      )}
      style={width ? { width } : undefined}
      title={title}
    >
      {label}
      {onResize && <ResizeHandle onMouseDown={onResize} />}
    </th>
  )
}

/** Table header row that renders sortable columns by default */
export function TableHeader({
  columns,
  sortBy,
  sortDir,
  onSort,
  widths,
  onResize,
}: {
  columns: TableColumn[]
  sortBy: string
  sortDir: 'asc' | 'desc'
  onSort: (key: string) => void
  widths?: Record<string, number | undefined>
  onResize?: (key: string) => (e: React.MouseEvent<HTMLElement>) => void
}) {
  return (
    <thead className="bg-gray-50">
      <tr>
        {columns
          .filter(col => !col.hidden)
          .map(col => {
            const colWidth = widths?.[col.key]
            const resizeHandler =
              col.resizable !== false && onResize ? onResize(col.key) : undefined
            return col.sortable === false ? (
              <StaticTh
                key={col.key}
                label={col.label}
                className={col.className}
                align={col.align}
                width={colWidth}
                onResize={resizeHandler}
                title={col.title}
              />
            ) : (
              <SortableTh
                key={col.key}
                label={col.label}
                sortKey={col.key}
                currentSort={sortBy}
                currentDir={sortDir}
                onSort={onSort}
                className={col.className}
                align={col.align}
                width={colWidth}
                onResize={resizeHandler}
                title={col.title}
              />
            )
          })}
      </tr>
    </thead>
  )
}

/** Skeleton for table rows */
export function TableSkeleton({
  rows = 5,
  cols = 5,
  className,
}: {
  rows?: number
  cols?: number
  className?: string
}) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-6 py-3">
                <Skeleton className="h-3 w-16" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <tr key={rowIdx}>
              {Array.from({ length: cols }).map((_, colIdx) => (
                <td key={colIdx} className="px-6 py-4">
                  <Skeleton className={cn('h-4', colIdx === 0 ? 'w-32' : 'w-16')} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Skeleton for company cards in overview grid */
export function CompanyCardSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-4 w-4 rounded" />
      </div>
      <div className="space-y-3">
        <div className="flex justify-between">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
        <div className="flex justify-between">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </div>
  )
}

/** Skeleton for the pipeline funnel */
export function PipelineFunnelSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <Skeleton className="h-4 w-48 mb-4" />
      <div className="flex items-center justify-between gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <React.Fragment key={i}>
            <div className="flex-1 min-w-[120px] rounded-lg border border-gray-100 p-4 text-center">
              <Skeleton className="h-8 w-12 mx-auto mb-2" />
              <Skeleton className="h-3 w-16 mx-auto" />
            </div>
            {i < 3 && <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

/** Container that handles table loading states smoothly.
 *  - Initial load (no data): shows skeleton
 *  - Re-fetch (has data): dims content, keeps it visible
 *  - Empty after load: shows empty message
 */
export function TableContainer({
  loading,
  hasData,
  skeletonRows = 8,
  skeletonCols = 6,
  emptyMessage = 'No data found',
  children,
}: {
  loading: boolean
  hasData: boolean
  skeletonRows?: number
  skeletonCols?: number
  emptyMessage?: React.ReactNode
  children: React.ReactNode
}) {
  if (loading && !hasData) {
    return <TableSkeleton rows={skeletonRows} cols={skeletonCols} />
  }

  if (!loading && !hasData) {
    if (typeof emptyMessage === 'string') {
      return <div className="text-center py-12 text-gray-500">{emptyMessage}</div>
    }
    return <>{emptyMessage}</>
  }

  return (
    <div
      className={cn(
        'transition-opacity duration-150',
        loading ? 'opacity-50 pointer-events-none' : 'opacity-100',
      )}
    >
      {children}
    </div>
  )
}
